/*!
* %productTitle%
* @version: %updateVersion%
* @license: %copyright%
*/
import 'cxbus';
import 'localstorage';
import 'auth';
import 'identifiers';
import 'messenger-helper';
import 'thirdparty-helper';
import 'database';
import 'markdown';
import 'support-center-helper';
import isAllowedDomain from 'domain-check';
import ServiceDiscovery from 'service-discovery';
import ServiceDiscoveryPlugin from 'service-discovery-plugin';
import { isThirdPartyStorageEnabled, setPublicConfig, getPublicConfig, getStoragePlugin } from 'util';
import { newRelicErrorMetrics } from './newrelic/newrelic-errors';

// Per-Product Init Scripts
import initThirdParty from 'init-thirdparty';
import initMessenger from 'init-messenger';
import initJourney from 'init-journey';
import initSupportCenterHelper from 'init-support-center-helper';
import initOffers from 'init-offers';
import initCobrowse from 'init-cobrowse';
import initVideo from 'init-video';
import initAuth from 'init-auth';

// Names/Codes
const GENESYS_JS = 'GenesysJS';
const STATUS_ACTIVE = 'Active';

// Data
const DATA_DEPLOYMENT_CONFIG = 'deploymentConfig';
const DATA_SNIPPET_CONFIG = 'snippetConfig';
const DATA_DOMAINS = 'domains';
const DATA_PUBLIC_DEPLOYMENT_CONFIG = 'configuration'; // WEBMESS-861: Limited public decentralized config that we provide to customers, who can use this to build their own UI with headless mode.

// Events
const EVENT_DOMAIN_NOT_ALLOWED = 'domainNotAllowed';
const EVENT_CONFIG_RECEIVED = 'configurationReceived';
const EVENT_DOMAINS_RECEIVED = 'domainsReceived';
const EVENT_MALFORMED_JSON = 'malformedJSON';
const EVENT_3RD_PARTY_STORAGE_ENABLED = 'thirdPartyStorageEnabled';
const EVENT_3RD_PARTY_STORAGE_DISABLED = 'thirdPartyStorageDisabled';


// Commands
const COMMAND_GET_CONFIG = 'getConfig';
const COMMAND_INIT = 'init';
const COMMAND_PUBLIC_CONFIG = 'configuration';

// Errors
const ERROR_STARTUP_PARAMETERS_MISSING = 'GenesysJS requires a deploymentId and environment to load.';
const ERROR_SERVICE_DISCOVERY_FAIL = 'GenesysJS could not initialise service discovery.';
const ERROR_GET_CONFIG_PERMISSIONS = 'You are not allowed to call this command.';
const ERROR_MALFORMED_JSON = 'Malformed JSON received.';
const ERROR_CONFIG_RETRIEVAL_FAIL = 'Could not retrieve deployment config.';
const ERROR_ALREADY_INITIALIZED = 'GenesysJS products are already initialized.';
const ERROR_ALREADY_AUTHENTICATED = 'User is already authenticated.';
const ERROR_CONFIG_REQUIRED = 'Initializing GenesysJS requires configuration.';
const ERROR_CONFIG_NOT_AVAILABLE = 'Deployment config is not available';
const ERROR_LOADING_PLUGIN = 'Error loading the plugin';
const ERROR_LOADING_OFFERS_PLUGIN = 'Error loading the offers plugin';


// grab config options from the snippet, if any
const snippetConfig = Genesys.c || {};
const {
  environment,
  deploymentId,
  preview,
  debug,
  debugConfig,
  debugConfigURL,
  regionalSubdomain,
} = snippetConfig;
const sStoragePlugin = getStoragePlugin();


// Check for startup parameters like deployment ID, environment and if its not in preview mode
if ((!environment || !deploymentId) && !preview) {
  throw new Error(ERROR_STARTUP_PARAMETERS_MISSING);
}

// Setup serviceDiscovery
let serviceDiscovery;

// Third party storage check;
const bThirdPartyStorage = false; // Not for GA

// We provide decentralized config to customers for headless mode. Remove the following properties from it.
const removeConfigProps = ['cobrowse', 'supportCenter', 'position', 'messenger.launcherButton', 'messenger.styles', 'messenger.apps.conversations.markdown', 'messenger.homeScreen', 'messenger.apps.conversations.humanize'];

try {
  serviceDiscovery = new ServiceDiscovery(environment, regionalSubdomain);
} catch (err) {
  throw new Error(ERROR_SERVICE_DISCOVERY_FAIL, err);
}


// configure CXBus for genesys.js context
Genesys('configure', {
  name: 'GenesysJS',
  pluginspath: `${serviceDiscovery.getUri('staticAssets')}/genesys-bootstrap/plugins/`, // Absolute plugins path
  pluginmap: {
    Journey: serviceDiscovery.getUri('journeyPlugin'),
    SupportCenter: serviceDiscovery.getUri('supportCenterPlugin'),
    OffersHelper: serviceDiscovery.getUri('offersHelper'),
    CobrowseService: serviceDiscovery.getUri('cobrowseService'),
    VideoHelper: serviceDiscovery.getUri('videoHelper'),
    VideoService: serviceDiscovery.getUri('videoService'),
    GenesysVendors: serviceDiscovery.getUri('vendorsPlugin'),
    //Launcher: serviceDiscovery.getUri('messengerMain'), // plugin
    //Engage: serviceDiscovery.getUri('messengerEngage'), // plugin
  },
  modulemap: {
    'genesyscloud-messaging-transport': serviceDiscovery.getUri('messagingTransport'),
  },
  childDomain: serviceDiscovery.getUri('staticAssets'),
  parentDomain: window.location.origin,
  debug: !!debug,
});


// Register serviceDiscovery
ServiceDiscoveryPlugin.register(serviceDiscovery);

// Register GenesysJS plugin
// Doing this allows for automatic logging in the browser
// console of all actions conducted by GenesysJS.
Genesys('registerPlugin', GENESYS_JS, (GenesysJS) => {
  // Create shortcut for getting/setting values in the data model
  const $$ = GenesysJS.data;

  // Local variables
  let bProductsInitialized = false; // determines if products are initialized
  let bCoreProductsInitialized = false; // determines if minimum required products like Journey are initialized irrespective of Auth enabled.

  const pluginMap = {
    messenger: 'Messenger',
    journey: 'Journey',
    auth: 'Auth',
    supportCenter: 'SupportCenter',
    offers: 'OffersHelper',
    cobrowse: 'CobrowseService',
    video: 'VideoHelper',
    thirdparty: 'ThirdPartyHelper',
  };

  // Make the snippet config available in the data model
  $$(DATA_SNIPPET_CONFIG, snippetConfig);

  const { subscribeNewRelicReady, newRelicError } = newRelicErrorMetrics() || {};

  subscribeNewRelicReady(); /**subscribe to NewRelic ready event before calling the NewRelic.error command */

  if (bThirdPartyStorage) {
    //Check if 3rd party storage is enabled
    isThirdPartyStorageEnabled().then((sResult)=>{
      if (sResult === 'true') {
        GenesysJS.publish(EVENT_3RD_PARTY_STORAGE_ENABLED);
      } else {
        GenesysJS.publish(EVENT_3RD_PARTY_STORAGE_DISABLED);
      }
    });
  }

  // local functions

  function handleLoadError(error, pluginName) {
    const oError = {
      errorName: ERROR_LOADING_PLUGIN,
      errorMessage: error,
      pluginName,
      deploymentId: deploymentId,
    };

    newRelicError(oError);
    GenesysJS.log('Error loading the plugin', error);
  }

  function initializeCoreProducts(config) {
    bCoreProductsInitialized = true;

    try {
      initJourney(config, GenesysJS);
    } catch (error) {
      handleLoadError(error, pluginMap.journey);
    }
  }

  function initializeProducts(config) {
    bProductsInitialized = true;

    try {
      initMessenger(config, GenesysJS);
    } catch (error) {
      handleLoadError(error, pluginMap.messenger);
    }
    try {
      initSupportCenterHelper(config, GenesysJS);
    } catch (error) {
      handleLoadError(error, pluginMap.supportCenter);
    }
    try {
      initCobrowse(config, GenesysJS);
    } catch (error) {
      handleLoadError(error, pluginMap.cobrowse);
    }
    try {
      initVideo(config, GenesysJS);
    } catch (error) {
      handleLoadError(error, pluginMap.video);
    }
    try {
      initAuth(config, GenesysJS);
    } catch (error) {
      handleLoadError(error, pluginMap.auth);
    }
    try {
      initOffers(config, GenesysJS);
    } catch (error) {
      handleLoadError(error, pluginMap.offers);
    }
  }

  /**
   * stepUp conversation: when authCode is available, fetch jwt
   * @param {*} config
   */
  function getAuthTokenAndInitProducts(config) {
    GenesysJS.subscribe('Auth.ready', () => { // wait for the brand AuthProvider plugin
      GenesysJS.command('Auth.getTokens', config).then((data) => {
        const { jwt } = data || {};

        if (jwt) {
          initializeProducts(config);
        } else {
          GenesysJS.log('Authentication plugin is ready. Tokens are not available yet.');
        }
      }, () => {
        GenesysJS.log('Authentication plugin is ready. Tokens are not available yet.');
      }).catch(()=>{});
    });
  }

  // Register the getConfig command, which fetches the domains.json and config.json files
  GenesysJS.registerCommand(COMMAND_GET_CONFIG, (e) => {
    // Apply permissions to this command
    if (e.commander !== GENESYS_JS) {
      const oError = {
        errorName: ERROR_GET_CONFIG_PERMISSIONS,
        errorMessage: ERROR_GET_CONFIG_PERMISSIONS,
        pluginName: GENESYS_JS,
      };

      newRelicError(oError);
      e.reject(ERROR_GET_CONFIG_PERMISSIONS);

      return;
    }

    // Debug Options
    if (debug === true) {
      // Debug mode for testing local configuration objects
      // Also bypasses domains check
      if (typeof debugConfig === 'object') {
        $$(DATA_DEPLOYMENT_CONFIG, debugConfig);
        e.resolve(debugConfig);

        return;
      }

      // Debug mode for testing remote configuration JSON files
      // Also bypasses domains check
      if (typeof debugConfigURL === 'string') {
        Genesys('loadJSON', debugConfigURL, (response) => {
          const deploymentConfig = JSON.parse(response);

          $$(DATA_DEPLOYMENT_CONFIG, deploymentConfig);
          e.resolve(deploymentConfig);
        });

        return;
      }
    }

    // Fetch the domains.json file for this deployment to first verify if the current domain is allowed access
    Genesys('loadJSON', `${serviceDiscovery.getUri('configs')}/${deploymentId}/domains.json`, (domainsResponse) => {
      try {
        // Parse the JSON response into a local object
        const oDomains = JSON.parse(domainsResponse);

        // Store the resulting domains list in the data model
        $$(DATA_DOMAINS, oDomains);

        GenesysJS.publish(EVENT_DOMAINS_RECEIVED);

        // Check the current domain against the allowed domains list
        if (isAllowedDomain(oDomains, document.location.hostname)) {
          // Fetch the config.json file for this deployment
          Genesys('loadJSON', `${serviceDiscovery.getUri('configs')}/${deploymentId}/config.json`, (configResponse) => {
            try {
              // Parse the JSON response into a local object
              const deploymentConfig = JSON.parse(configResponse);

              if (deploymentConfig && Object.keys(deploymentConfig).length) {

                // Store the resulting configuration in the data model
                $$(DATA_DEPLOYMENT_CONFIG, deploymentConfig);

                // set decentralized config for brand. We will include this publicConfig in our SDK for headless mode.
                setPublicConfig(deploymentConfig, removeConfigProps).then((pubConfig) => {
                  // Store public deployement config. We will document this for customers.
                  $$(DATA_PUBLIC_DEPLOYMENT_CONFIG, pubConfig);
                });

                // Resolve and return the configuration data
                e.resolve(deploymentConfig);

              } else {

                const oError = {
                  errorName: ERROR_CONFIG_NOT_AVAILABLE,
                  errorMessage: ERROR_CONFIG_NOT_AVAILABLE,
                  pluginName: GENESYS_JS,
                  deploymentId: deploymentId || '',
                };
        
                newRelicError(oError);
                e.reject(ERROR_CONFIG_NOT_AVAILABLE);
              }
            } catch (err) {
              const oError = {
                errorName: ERROR_MALFORMED_JSON,
                errorMessage: err,
                pluginName: GENESYS_JS,
                deploymentId: deploymentId || '',
              };

              newRelicError(oError);
              // Reject and throw an error in the console
              e.reject(ERROR_MALFORMED_JSON, err);

              throw new Error(err);
            }
          }, (err) => {
            const oError = {
              errorName: ERROR_CONFIG_RETRIEVAL_FAIL,
              errorMessage: err,
              pluginName: GENESYS_JS,
              deploymentId: deploymentId || '',
            };

            newRelicError(oError);
            // Reject and throw an error in the console
            e.reject(ERROR_CONFIG_RETRIEVAL_FAIL, err);
          });

        // Publish domain error
        } else GenesysJS.publish(EVENT_DOMAIN_NOT_ALLOWED);
      } catch (err) {
        const oError = {
          errorName: ERROR_MALFORMED_JSON,
          errorMessage: err,
          pluginName: GENESYS_JS,
          deploymentId: deploymentId || '',
        };

        newRelicError(oError);
        // Reject and throw an error in the console
        e.reject(ERROR_MALFORMED_JSON, err);

        //Publish malformed JSON error
        GenesysJS.publish(EVENT_MALFORMED_JSON, err);

        throw new Error(err);
      }
    });
  });

  /**
   * Command to initialize GenesysJS products.
   * Usage: Typically, it can be used when page RELOAD DOESN'T happen after successful Authentication.
   * Example: Google Authentication
   * @resolve { jwt }
   */
  GenesysJS.registerCommand(COMMAND_INIT, (e) => {
    const oConfig = {
      deploymentConfig: $$(DATA_DEPLOYMENT_CONFIG),
      snippetConfig: $$(DATA_SNIPPET_CONFIG),
    };

    const { auth } = oConfig.deploymentConfig;
    const { enabled: bAuthEnabled, allowSessionUpgrade } = auth || {};
    const bStepUpConversation = bAuthEnabled && !!allowSessionUpgrade;
    const bAuthenticated = $$('Auth.authenticated') || false;
    const bLoggingIn = $$('Auth.signingIn') || false;
    const bSessionUpgrade = bStepUpConversation && bLoggingIn;

    if (!bCoreProductsInitialized && oConfig.deploymentConfig) { // Always initialize core products like Journey
      initializeCoreProducts(oConfig);
    }

    if (!bProductsInitialized && (!bAuthEnabled || bStepUpConversation)) {
      if (oConfig && oConfig.deploymentConfig) {
        initializeProducts(oConfig);
        e.resolve();
      } else {
        const oError = {
          errorName: ERROR_CONFIG_REQUIRED,
          errorMessage: ERROR_CONFIG_REQUIRED,
          pluginName: GENESYS_JS,
          deploymentId: deploymentId,
        };

        newRelicError(oError);
        e.reject(ERROR_CONFIG_REQUIRED);
      }
    } else {/**WEBMESS-540: if products are already loaded, check if auth is enabled and not already authenticated if yes, get auth code and reinitialize, else reject the command */
      if (bAuthEnabled) {
        if (!bAuthenticated || bSessionUpgrade) {// if user not authenticated, fetch jwt
          GenesysJS.command('Auth.getTokens', oConfig).then((data) => {
            const { jwt } = data || {};

            if (jwt) {
              initializeProducts(oConfig);
            } else {
              GenesysJS.log('Authentication plugin is ready. Tokens are not available yet.');
            }
            e.resolve(data); // resolve with jwt
          }, () => {
            GenesysJS.log('Authentication plugin is ready. Tokens are not available yet.');
            e.resolve();
          }).catch(()=>{});
        } else {/**If already authenticated */
          const oError = {
            errorName: ERROR_ALREADY_AUTHENTICATED,
            errorMessage: ERROR_ALREADY_AUTHENTICATED,
            pluginName: GENESYS_JS,
            deploymentId: deploymentId,
          };

          newRelicError(oError);
          e.reject(ERROR_ALREADY_AUTHENTICATED);
        }
      } else {
        const oError = {
          errorName: ERROR_ALREADY_INITIALIZED,
          errorMessage: ERROR_ALREADY_INITIALIZED,
          pluginName: GENESYS_JS,
          deploymentId: deploymentId,
        };

        newRelicError(oError);
        e.reject(ERROR_ALREADY_INITIALIZED);
      }
    }
  });

  /**
   * Register command that returns public config for customers using in headless mode
   */
  GenesysJS.registerCommand(COMMAND_PUBLIC_CONFIG, (e) => {
    const deployConfig = $$(DATA_DEPLOYMENT_CONFIG);
    const snippetConfig = $$(DATA_SNIPPET_CONFIG);

    const {
      deploymentId
    } = snippetConfig;

    if (deployConfig && Object.keys(deployConfig).length) {
      const publicConfig = getPublicConfig();

      if (publicConfig && Object.keys(publicConfig).length) {
        e.resolve(publicConfig);
      } else {
        const oError = {
          errorName: ERROR_CONFIG_NOT_AVAILABLE,
          errorMessage: ERROR_CONFIG_NOT_AVAILABLE,
          pluginName: GENESYS_JS,
          deploymentId: deploymentId || ''
        };

        newRelicError(oError);
        e.reject(ERROR_CONFIG_NOT_AVAILABLE);
      }
    } else {
      const oError = {
        errorName: ERROR_CONFIG_NOT_AVAILABLE,
        errorMessage: ERROR_CONFIG_NOT_AVAILABLE,
        pluginName: GENESYS_JS,
        deploymentId: deploymentId || ''
      };

      newRelicError(oError);
      e.reject(ERROR_CONFIG_NOT_AVAILABLE);
    }
  });

  // Check if its not preview mode and fetch the config
  if (!preview) {

    // Initialize thirdParty plugin like NewRelic always to send any error events to NewRelic incase config fails to load, etc.
    try {
      initThirdParty({}, GenesysJS);
    } catch (error) {
      handleLoadError(error, pluginMap.thirdparty);
    }

  // Call the getConfig command and init all additional products
    GenesysJS.command(COMMAND_GET_CONFIG).then((deploymentConfig) => {
      GenesysJS.log('Configuration Received', deploymentConfig);

      // Combine the snippet config and deployment config
      const config = {
        deploymentConfig,
        snippetConfig,
      };

      // Check for Authentication
      const { status, auth } = config.deploymentConfig;
      const { enabled: bAuthEnabled, allowSessionUpgrade } = auth || {};

      // Setup a republish event for receiving the configuration data.
      // Using republish() rather than publish() makes one-time events like this
      // publish their event again to any new subscribers. This way, it doesn't
      // matter when you subscribe to this event.
      GenesysJS.republish(EVENT_CONFIG_RECEIVED, config);

      // If the deployment is active, initialize supported products
      if (status && status === STATUS_ACTIVE) {
        initializeCoreProducts(config); // always initialize core products like Journey irrespective of Auth enabled

        if (bAuthEnabled && bAuthEnabled === true) { // check if Authentication is needed and fetch jwt
          let sStoredAuthTkn = GenesysJS.data('Auth.jwt');

          if (sStoredAuthTkn) { // check if Auth token is already available and initialize the products, else no
            initializeProducts(config);
          } else {
            if (allowSessionUpgrade) {
              GenesysJS.subscribe('LocalStorage.ready', () => {
                // stepUp conversation: when authCode is available, fetch jwt
                // When allow session upgrade is enabled, subscribe to LocalStorage ready event before initializing products. 
                // This is to fix where we aren't getting LogoutEvent in WS from Shyrka.
                setTimeout(() => {
                  GenesysJS.command(`${sStoragePlugin}.get`, { name: `${deploymentId}:authenticating` }).then((authenticating) => {
                    if (authenticating === 'true') {
                      getAuthTokenAndInitProducts(config);
                    } else {
                      GenesysJS.command(`${sStoragePlugin}.get`, { name: `${deploymentId}:signingIn` }).then((signingIn) => {
                        if (signingIn === 'true') {
                          getAuthTokenAndInitProducts(config);
                        } else {
                          initializeProducts(config);
                          GenesysJS.log('Conversation Session can later be stepped up to authenticated session.');
                        }
                      });
                    }
                  });
                }, 200);
              });
            } else {
              GenesysJS.subscribe('Auth.ready', () => { // wait for the brand AuthProvider plugin
                // WEBMESS-1632: For normal Authenticated Messenger, subscribe to LocalStorage ready event before initializing.
                // This ensures we can check if auth tokens stored in the local storage are available.
                GenesysJS.subscribe('LocalStorage.ready', () => {
                  setTimeout(() => {
                    if (!bProductsInitialized)
                      getAuthTokenAndInitProducts(config);
                  }, 200); // Adding this as the existing cdls library takes this time to initialize properly along with data availability.
                });
              });
            }
          }

          // This event is published after LocalStorage plugin is ready and Auth plugin authenticated. Subscribing to it to get the jwt from storage and restore Messenger.
          GenesysJS.subscribe('Auth.authenticated', () => {
            if (!bProductsInitialized) {
              sStoredAuthTkn = GenesysJS.data('Auth.jwt');
              if (sStoredAuthTkn) {
                initializeProducts(config);
              }
            }
          });
        } else { // normal ephimeral conversation
          initializeProducts(config);
        }
      }

      // Execute items inside the queue after CXBus, core plugins, and the configuration are loaded.
      GenesysJS.ready();
      Genesys('executeQueue');
    }, () => {
    // executeQueue() tells CXBus to execute any customer activity that was queued.
    // This must always execute or else their code will never run. Even if fetching
    // the configuration fails, we should let their code run.
      GenesysJS.ready();
      Genesys('executeQueue');
    });
  } else {
    const previewConfig = {
      deploymentConfig: {
        messenger: {
          enabled: true,
          preview: true,
        },
      },
    };

    // Init Messenger with preview config
    initMessenger(previewConfig, GenesysJS);

    // Init the thirdparty iframe in preview mode
    // downstream plugins will follow preview flag
    initThirdParty(previewConfig, GenesysJS);


    GenesysJS.ready();
    Genesys('executeQueue');
  }
});
